import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { makeApi } from "@/lib/api";
import { AddDriverDialog } from "@/components/drivers/AddDriverDialog";
import { CredentialsDialog } from "@/components/drivers/CredentialsDialog";
import { DriverRow } from "@/components/drivers/DriverRow";
import { LicenseAlerts } from "@/components/drivers/LicenseAlerts";

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => `${x.loc?.slice(-1)[0] || ""}: ${x.msg}`).join(" · ") : d?.toString() || "Something went wrong";
};

export default function Drivers() {
  const { getToken } = useAuth();
  const api = makeApi(getToken);
  const [drivers, setDrivers] = useState(null);
  const [adding, setAdding] = useState(false);
  const [creds, setCreds] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/drivers");
      setDrivers(data);
    } catch (e) {
      setDrivers((prev) => prev ?? []);
      toast.error(`Could not load drivers: ${errMsg(e)}`);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const addDriver = async (body) => {
    try {
      await api.post("/admin/drivers", body);
      setAdding(false);
      setCreds({ title: "Driver created", username: body.username, password: body.password });
      refresh();
      return null;
    } catch (e) { return errMsg(e); }
  };

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Drivers</h1>
          <p className="text-sm text-slate-500 mt-1">You create each driver's username and password. They only see loads you assign them.</p>
        </div>
        <Button onClick={() => setAdding(true)} className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="add-driver-btn">
          <UserPlus className="w-4 h-4 mr-1.5" /> Add driver
        </Button>
      </div>

      <LicenseAlerts drivers={drivers} />

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="hidden sm:table-cell text-left px-4 py-3 font-medium">Phone</th>
                <th className="hidden sm:table-cell text-left px-4 py-3 font-medium">Active loads</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {drivers === null && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {drivers?.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400" data-testid="drivers-empty">No drivers yet. Tap “Add driver” to create the first login.</td></tr>
              )}
              {drivers?.map((d) => <DriverRow key={d.user_id} driver={d} />)}
            </tbody>
          </table>
        </div>
      </Card>

      <AddDriverDialog open={adding} onClose={() => setAdding(false)} onSubmit={addDriver} />
      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />
    </div>
  );
}
